'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { GOOGLE_ANALYTICS_NAME, useConsent } from '@lichtblick/consent';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getStateFromStorage } from './tracker.helpers';
const ENVIRONMENTS = {
    prod: 'productive',
    test: 'staging',
    dev: 'development'
};
const environment = ENVIRONMENTS[process.env.NEXT_PUBLIC_STAGE] ?? 'development';
const localEventStores = {
    virtPath: [],
    'Page Meta': [],
    NewgaEvent: [],
    gaEvent: [],
    Ecommerce: []
};
const clearAll = ()=>Object.values(localEventStores).forEach((store)=>{
        store.length = 0;
    });
export const trackEvent = (event, eventStoreName, shouldAvoidDuplicate, serializer)=>{
    const eventStore = localEventStores[eventStoreName];
    const serializedEvent = shouldAvoidDuplicate && serializer?.(event);
    if (serializedEvent && eventStore.some((last)=>serializedEvent === serializer?.(last))) {
        return;
    }
    // virt path event indicates page load, clear all events
    if (event.event === 'virtPath') {
        clearAll();
    }
    window.dataLayer.push(event);
    eventStore.push(event);
};
export const createPageMetaEvent = ({ contentGroup, contentGroup2, contentGroup3, customerTypeUser, loginStatus, pageArea, pageType })=>{
    const trafficType = typeof document === 'undefined' || environment !== 'productive' || document.cookie.includes('trafficType=internal') ? 'internal' : 'external';
    return {
        event: 'Page Meta',
        environment,
        // GA3
        pageArea,
        pageType,
        pageEnvironment: 'new frontend-design',
        trafficType,
        customerTypeUser,
        loginStatus,
        // GA4
        page_area: pageArea,
        content_group: contentGroup,
        content_group2: contentGroup2,
        content_group3: contentGroup3,
        page_type: pageType,
        traffic_type: trafficType,
        customer_type: customerTypeUser,
        login_status: loginStatus
    };
};
const flushQueue = (trackingParams)=>{
    trackingParams.forEach(({ event, eventStoreName, serializer, shouldAvoidDuplicate })=>{
        trackEvent(event, eventStoreName, shouldAvoidDuplicate, serializer);
    });
    return [];
};
const useSetupTracker = ()=>{
    const consent = useConsent(GOOGLE_ANALYTICS_NAME);
    const [, setTrackingQueue] = useState([]);
    const isTrackingEnabled = consent;
    const [isVirtPathTracked, setVirtPathTracked] = useState(false);
    const trackOrQueueEvent = useMemo(()=>isTrackingEnabled ? trackEvent : (event, eventStoreName, shouldAvoidDuplicate, serializer)=>setTrackingQueue((queue)=>[
                    ...queue,
                    {
                        event,
                        eventStoreName,
                        shouldAvoidDuplicate,
                        serializer
                    }
                ]), [
        isTrackingEnabled
    ]);
    useEffect(()=>{
        if (isTrackingEnabled) {
            setTrackingQueue(flushQueue);
        }
    }, [
        isTrackingEnabled
    ]);
    return useMemo(()=>({
            isTrackingEnabled,
            trackEcommerceEvent: (event)=>{
                const serializer = (event)=>JSON.stringify({
                        ...event,
                        'gtm.uniqueEventId': undefined
                    });
                if (event) {
                    trackOrQueueEvent({
                        ...event
                    }, 'Ecommerce', true, serializer);
                }
            },
            trackCustomEvent: (tracking, legacyTracking, shouldAvoidDuplicate = true)=>{
                if (tracking) {
                    const serializer = (event)=>JSON.stringify({
                            ...event,
                            'gtm.uniqueEventId': undefined
                        });
                    // GA4
                    trackOrQueueEvent({
                        event: 'NewgaEvent',
                        event_name: tracking.eventName,
                        [tracking.eventName]: tracking.customProps,
                        ...tracking.sha256_email_address ? {
                            sha256_email_address: tracking.sha256_email_address
                        } : {}
                    }, 'NewgaEvent', shouldAvoidDuplicate, serializer);
                    // GA3
                    if (legacyTracking) {
                        trackOrQueueEvent({
                            event: 'gaEvent',
                            ...legacyTracking
                        }, 'gaEvent', shouldAvoidDuplicate, serializer);
                    }
                }
            },
            trackPageMetaEvent: ({ contentGroup, contentGroup2, contentGroup3, pageArea, pageType })=>{
                const { customerTypeUser = 'new customer', loginState = 'logged out' } = getStateFromStorage() ?? {};
                trackOrQueueEvent(createPageMetaEvent({
                    customerTypeUser,
                    loginStatus: loginState,
                    contentGroup,
                    contentGroup2,
                    contentGroup3,
                    pageArea,
                    pageType
                }), 'Page Meta', true, ({ event })=>event);
            },
            /** see e.g. https://docs.google.com/presentation/d/11AEsa5noYLepG_XwfP7TzBMyVgsEB-CwR371ujo0aGs/edit#slide=id.g1224e2b2ed8_0_27 */ trackVirtPathEvent: (virtPathInput)=>{
                trackOrQueueEvent({
                    ...virtPathInput,
                    event: 'virtPath'
                }, 'virtPath', true, ({ virtPagePath })=>virtPagePath);
                setVirtPathTracked(localEventStores.virtPath.length > 0);
            },
            isVirtPathTracked: isVirtPathTracked
        }), [
        isTrackingEnabled,
        isVirtPathTracked,
        trackOrQueueEvent
    ]);
};
const TrackingContext = /*#__PURE__*/ createContext({
    isTrackingEnabled: false,
    trackEcommerceEvent: ()=>{
    // noop
    },
    trackCustomEvent: ()=>{
    // noop
    },
    trackVirtPathEvent: ()=>{
    // noop
    },
    trackPageMetaEvent: ()=>{
    // noop
    },
    isVirtPathTracked: false
});
export const TrackerProvider = ({ children })=>{
    const trackingState = useSetupTracker();
    return /*#__PURE__*/ _jsx(TrackingContext.Provider, {
        value: trackingState,
        children: children
    });
};
export const useTracker = ()=>useContext(TrackingContext);
export * from './TrackingScript';
// Expose to allow setting meta information across all apps
export { setStateInStorage } from './tracker.helpers';
export * from './tracker.types';

//# sourceMappingURL=index.js.map